
"use client";
import {
  Navbar,
  NavBody,
  MobileNav,
  NavbarLogo,
  NavbarButton,
  MobileNavHeader,
  MobileNavToggle,
  MobileNavMenu,
} from "../ui/resizable-navbar";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, LogIn, Mail, Phone, X, Calendar } from "lucide-react";
import axiosInstance from "../../utils/axiosInstance";
import { getToken, getSessionEpoch } from "../../utils/authStorage";
import { API_ENDPOINTS } from "../../constants/config";
import { motion } from "framer-motion";

export function AdvancedNavbarExample({ isAuthenticated, user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [schoolInfo, setSchoolInfo] = useState({
    name: "",
    logo: "",
  });
  const [isSchoolInfoLoading, setIsSchoolInfoLoading] = useState(false);

  const userRole = user?.role;
  const isSuperAdmin = userRole === "superadmin";
  const isSuperSchoolAdmin = userRole === "superchooladmin";
  const shouldShowSchoolIdentity = !isSuperAdmin && !isSuperSchoolAdmin;
  // null means "nothing to show yet" — rendered as a loading skeleton while
  // the fetch is in flight, or nothing at all once it's done and there's
  // genuinely no name (no generic "School Dashboard" fallback text).
  const schoolTitle =
    (isSuperAdmin && "Super Admin Portal") ||
    (isSuperSchoolAdmin && "Super Customer Admin") ||
    schoolInfo.name ||
    null;

  useEffect(() => {
    if (isAuthenticated && shouldShowSchoolIdentity) {
      // Drop any previous user's name immediately instead of leaving it on
      // screen until the new fetch resolves.
      setSchoolInfo({ name: "", logo: "" });
      setIsSchoolInfoLoading(true);
      fetchSchoolInfo();
    } else if (!isAuthenticated) {
      setSchoolInfo({ name: "", logo: "" });
      setIsSchoolInfoLoading(false);
    }
  }, [isAuthenticated, shouldShowSchoolIdentity]);


  const fetchSchoolInfo = async () => {
    // A logout/login happening while this request is in flight means the
    // response, whenever it lands, belongs to a session that's no longer
    // live — applying it would show the previous user's school name.
    const epochAtRequest = getSessionEpoch();
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(API_ENDPOINTS.schoolAbout, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (getSessionEpoch() !== epochAtRequest) return;

      if (response.data.status === "success") {
        setSchoolInfo({
          name: response.data.data.school.name || "",
          logo: response.data.data.school.logo || "",
        });
      }
    } catch (error) {
      if (getSessionEpoch() !== epochAtRequest) return;
      if (error.response?.status === 404) {
        setSchoolInfo({
          name: "",
          logo: "",
        });
      }
    } finally {
      if (getSessionEpoch() === epochAtRequest) {
        setIsSchoolInfoLoading(false);
      }
    }
  };

  // Matches whatever text color the caller is using (white on the navbar,
  // brand blue in the mobile menu) via `currentColor`.
  const SchoolTitleSkeleton = () => (
    <span className="inline-block h-4 w-28 max-w-[40vw] animate-pulse rounded-full bg-current align-middle opacity-25" />
  );

  // Navigation items for unauthenticated users (landing page)
  const publicNavItems = [
    { name: "Products", section: "products", path: "/", hash: "#products" },
    { name: "Features", section: "features", path: "/features" },
    { name: "Benefits", section: "benefits", path: "/benefits" },
    { name: "Blog", section: "blog", path: "/blog" },
    { name: "FAQ", section: "faq", path: "/faq" },
    { name: "Contact", section: "contact", path: "/contact" },
  ];

  const scrollToSection = (sectionName) => {
    const element = document.getElementById(sectionName);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleNavClick = (item) => {
    if (!isAuthenticated && item.section) {
      if (location.pathname === item.path) {
        scrollToSection(item.section);
      } else {
        navigate(item.path);
        if (item.hash) {
          setTimeout(() => scrollToSection(item.section), 300);
        }
      }
    } else {
      navigate(item.path || item.link);
    }
    setIsMobileMenuOpen(false);
  };

  // Logged-in users get their real menu from the dashboard sidebar
  // (SchoolAdminDashboard2 / MENU_CATEGORIES) - this navbar only shows
  // the marketing links when nobody's logged in.
  const navItems = isAuthenticated ? [] : publicNavItems;

  const handleLogout = async () => {
    try {
      const token = getToken();
      await axiosInstance.post(
        API_ENDPOINTS.authLogout,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setIsMobileMenuOpen(false);
      onLogout();
    }
  };

  const handleLogin = () => {
    setIsMobileMenuOpen(false);
    navigate("/login");
  };

  return (
    <>
      <Navbar isFixed={isAuthenticated}>
        {/* Desktop Navigation */}
        <div className="w-full px-4">
          <NavBody>
            <div className="hidden items-center gap-3 sm:flex md:gap-4">
              {isAuthenticated ? (
                <>
                  {shouldShowSchoolIdentity && (
                    <motion.img
                      src={schoolInfo.logo || "/schoolicon.jpg"}
                      alt="School Logo"
                      className="h-8 w-8 rounded-full object-cover ring-2 ring-white"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    />
                  )}
                  <h1 className="line-clamp-1 text-left text-base font-extrabold text-white sm:text-lg md:text-xl lg:text-2xl">
                    {schoolTitle ?? (isSchoolInfoLoading ? <SchoolTitleSkeleton /> : null)}
                  </h1>
                </>
              ) : (
                <div className="flex items-center" onClick={() => navigate("/")}>
                  <NavbarLogo logo="/logo-no-bg.png" alt="Clynox" />
                </div>
              )}
            </div>
            
            {!isAuthenticated && (
              <div className="hidden lg:flex flex-1 items-center justify-center px-6">
                <div className="flex items-center gap-1 text-white">
                  {navItems.map((item, idx) => (
                    <motion.button
                      key={`nav-item-${idx}`}
                      onClick={() => handleNavClick(item)}
                      className="rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors duration-200 hover:bg-white/10 hover:text-gray-100"
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ type: "spring", stiffness: 400, damping: 17 }}
                    >
                      {item.name}
                    </motion.button>
                  ))}
                </div>
              </div>
            )}

            <div className="hidden items-center gap-3 sm:flex md:gap-4">
              {isAuthenticated ? (
                <div className="flex items-center" onClick={() => navigate("/")}>
                  <NavbarLogo logo="/logo-no-bg.png" alt="Clynox" />
                </div>
              ) : (
                <>
                  <motion.button
                    onClick={() => navigate("/schedule-demo")}
                    className="flex items-center gap-2 text-sm font-semibold bg-white text-[#0263cc] hover:bg-blue-50 px-4 py-2 rounded-lg transition-colors shadow-sm"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Book Demo
                  </motion.button>

                  <motion.button
                    onClick={handleLogin}
                    className="flex items-center gap-2 text-sm text-white hover:bg-[#0255b3] px-4 py-2 rounded-lg transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>Login</span>
                    <LogIn className="h-5 w-5" />
                  </motion.button>
                </>
              )}
            </div>
          </NavBody>
        </div>

        {/* Mobile Navigation */}
        <MobileNav>
          <div className="w-full px-4">
            <MobileNavHeader>
              <div className="flex items-center">
                <NavbarLogo logo="/logo-no-bg.png" alt="Clynox" />
              </div>

              <div className="flex items-center gap-2">
                {!isAuthenticated && (
                  <motion.button
                    onClick={handleLogin}
                    className="flex items-center gap-1 text-xs text-white hover:bg-[#0255b3] px-3 py-1.5 rounded-lg transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>Login</span>
                    <LogIn className="h-4 w-4" />
                  </motion.button>
                )}
                <MobileNavToggle
                  isOpen={isMobileMenuOpen}
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                />
              </div>
            </MobileNavHeader>
          </div>

          <MobileNavMenu
            isOpen={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
          >
            <div className="flex items-center justify-between border-b border-gray-200 pb-3">
              <h3 className="text-base font-semibold text-gray-900">Menu</h3>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* School Info Section (for authenticated users) */}
            {isAuthenticated && (
              <motion.div
                className="p-4 rounded-lg bg-[#0263cc]/5 border border-[#0263cc]/10"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center justify-center mb-3">
                  <h2 className="text-center text-lg font-bold text-[#0263cc]">
                    {schoolTitle ?? (isSchoolInfoLoading ? <SchoolTitleSkeleton /> : null)}
                  </h2>
                </div>
                {shouldShowSchoolIdentity && (
                  <div className="flex items-center justify-center border-t pt-3">
                    <motion.img
                      src={schoolInfo.logo || "/schoolicon.jpg"}
                      alt="School Logo"
                      className="h-16 w-16 rounded-full object-cover ring-2 ring-[#0263cc]"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    />
                  </div>
                )}
              </motion.div>
            )}

            {/* Main Navigation */}
            <div className="flex flex-col">
              {navItems.map((item, idx) => (
                <motion.button
                  key={`mobile-nav-${idx}`}
                  onClick={() => handleNavClick(item)}
                  className="w-full text-left px-3 py-2 rounded-lg text-gray-700 hover:bg-[#0263cc]/5 hover:text-[#0263cc] transition-colors font-medium text-sm"
                  whileHover={{ x: 4 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  {item.name}
                </motion.button>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 pt-3 border-t border-gray-200">
              {isAuthenticated ? (
                <NavbarButton
                  variant="secondary"
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Logout</span>
                </NavbarButton>
              ) : (
                <>
                  <NavbarButton
                    variant="primary"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate("/schedule-demo");
                    }}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <Calendar className="h-4 w-4" />
                    Book a Demo
                  </NavbarButton>
                  <NavbarButton
                    variant="secondary"
                    onClick={handleLogin}
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <LogIn className="h-4 w-4" />
                    <span>Login</span>
                  </NavbarButton>
                </>
              )}
            </div>
          </MobileNavMenu>
        </MobileNav>

        {isAuthenticated && (
          <div className="pb-2 text-center sm:hidden">
            {isSuperAdmin || isSuperSchoolAdmin ? (
              <h1 className="text-base font-bold text-white">{schoolTitle ?? (isSchoolInfoLoading ? <SchoolTitleSkeleton /> : null)}</h1>
            ) : (
              <h1 className="text-base font-bold text-white">
                {schoolTitle ?? (isSchoolInfoLoading ? <SchoolTitleSkeleton /> : null)}
              </h1>
            )}
          </div>
        )}

        {/* Info Bar (only for public pages) — sits below the blue banner */}
        {!isAuthenticated && (
          <div className="border-t border-white/10 bg-black px-4 py-1.5 text-white sm:py-2">
            <div className="flex w-full flex-row items-center justify-between text-xs sm:text-sm">
              <a
                href="mailto:clynoxofficial@gmail.com"
                className="flex items-center gap-2 text-white/95 transition-colors hover:text-white"
              >
                <Mail className="h-4 w-4" />
                <span>clynoxofficial@gmail.com</span>
              </a>
              <a
                href="tel:+917499070944"
                className="flex items-center gap-2 text-white/95 transition-colors hover:text-white"
              >
                <Phone className="h-4 w-4" />
                <span>+91-7499070944</span>
              </a>
            </div>
          </div>
        )}
      </Navbar>
    </>
  );
}

export default AdvancedNavbarExample;
